import { useState } from "react";
import { useTimer } from "@/contexts/TimerContext";
import { Play, Pause, Square } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/dialogs";

// Per-task start/pause/resume/stop. Respects the one-active-timer rule with a
// friendly "stop the other and start this?" confirm.
export function TaskTimerControls({ task, onChange }) {
  const t = useTimer();
  const [conflict, setConflict] = useState(null);
  if (!t) return null;
  if ((task.time_tracking || "off") === "off") return null;

  const isThis = t.isRunningTask(task.id);
  const running = isThis && t.timer.state === "running";
  const paused = isThis && t.timer.state === "paused";

  const doStart = async (force = false) => {
    try {
      const r = await t.start(task.id, { activityType: task.activity_type || "", force });
      if (!r.ok && r.conflict) { setConflict(r); return; }
      toast.success("Timer started");
      onChange?.();
    } catch (e) { toast.error(e.message); }
  };

  if (!isThis) {
    return (
      <>
        <button onClick={() => doStart(false)} data-testid={`start-timer-${task.id}`} title="Start timer"
          className="flex items-center gap-1 rounded-full border border-accent/40 bg-accent-subtle px-2 py-1 text-xs text-accent hover:bg-accent/10">
          <Play className="h-3 w-3" /> Start
        </button>
        <ConfirmDialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}
          title="A timer is already running"
          description={`You already have a timer running for “${conflict?.running_task_title}”. Stop it and start this one?`}
          confirmLabel="Stop & start this" onConfirm={async () => { setConflict(null); await doStart(true); }} />
      </>
    );
  }

  return (
    <div className="flex items-center gap-1" data-testid={`timer-controls-${task.id}`}>
      {running ? (
        <button onClick={async () => { await t.pause(); onChange?.(); }} data-testid={`pause-timer-${task.id}`} title="Pause"
          className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"><Pause className="h-3 w-3" /></button>
      ) : (
        <button onClick={async () => { await t.resume(); onChange?.(); }} data-testid={`resume-timer-${task.id}`} title="Resume"
          className="rounded-full border border-accent/40 px-2 py-1 text-xs text-accent"><Play className="h-3 w-3" /></button>
      )}
      <button onClick={async () => { await t.stop(); toast.success("Time saved"); onChange?.(); }} data-testid={`stop-timer-${task.id}`} title="Stop"
        className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground hover:text-destructive"><Square className="h-3 w-3" /></button>
      <span className={cn("font-mono text-xs tabular-nums", running ? "text-accent" : "text-muted-foreground")}>{isThis ? fmtLocal(t.display) : ""}</span>
    </div>
  );
}

function fmtLocal(s) {
  const m = Math.floor((s || 0) / 60); const sec = (s || 0) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}
